import { AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, FormArray, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CountdownComponent } from '@modules/common/components/countdown/countdown.component';
import { Subscription } from 'rxjs';

/**
 * This component is a ControlValueAccessor for MFA codes. It shows text boxes to enter the code
 * (one character each) and a countdown to the code's expiry. It also shows error alerts based
 * on the provided inputs.
 */
@Component({
    selector: 'app-login-mfa-code',
    templateUrl: './login-mfa-code.component.html',
    styleUrls: ['./login-mfa-code.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LoginMFACodeComponent),
            multi: true,
        }
    ]
})
export class LoginMFACodeComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
    @Input()
    public isInvalidCode!: boolean;

    @Input()
    public isExpiredRequest!: boolean;

    @Input()
    public isLockedOut!: boolean;

    @Input()
    public mfaTimeOutInMinutes: number = 5;

    @Output()
    public countdownTimedOut = new EventEmitter<any>();

    public formArray: FormArray<FormControl<string>>;

    @ViewChild(CountdownComponent, { static: true })
    private _loginCountdownComponent!: CountdownComponent;

    @ViewChildren('codeInput')
    private _inputElements!: QueryList<ElementRef<HTMLInputElement>>;

    private _subscriptions: Subscription[] = [];

    private _propagateChanges?: (value: string) => void;

    constructor() {
        this.formArray = this.buildFormArray();
    }

    public ngAfterViewInit(): void {
        this._loginCountdownComponent.countdownTo(new Date(new Date().getTime() + this.mfaTimeOutInMinutes * 60 * 1000));
    }

    public ngOnDestroy(): void {
        for (let subscription of this._subscriptions) {
            subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    public writeValue(value: string | undefined): void {
        value = (value || '').substring(0, this.formArray.controls.length);
        this.setMFACodeValue(value);
    }

    public registerOnChange(fn: (value: string | undefined) => void): void {
        this._propagateChanges = fn;
    }

    public registerOnTouched(fn: any): void {
    }

    public onCountdownCompleted(): void {
        this.countdownTimedOut.emit();
    }

    public onPaste(e: ClipboardEvent): void {
        e.preventDefault(); //Prevent the original pasting

        const mfaCode = e.clipboardData?.getData('text').trim().substring(0, this.formArray.controls.length) || '';

        //Write the MFA code to the controls, then propagate the new value.
        this.setMFACodeValue(mfaCode);
        this._propagateChanges?.(this.getMFACodeFromFormArray());

        //Move the cursor to either the last input or the first empty one.
        this.focusInput(Math.min(mfaCode.length, this.formArray.controls.length - 1));
    }

    public onKeydown(index: number, event: KeyboardEvent): void {
        //When pressing backspace in an empty input, move the cursor to the previous input
        //before the backspace actually takes effect, so the previous input's value gets
        //deleted instead.
        if (event.key === "Backspace" && index > 0 && !this.formArray.controls[index].value) {
            this.focusInput(index - 1);
        }
    }

    public onInput(index: number, event: InputEvent): void {
        //After entering one character of the MFA code, move the cursor to the next input.
        if (event.inputType !== 'deleteContentBackward' && index + 1 < this.formArray.controls.length) {
            this.focusInput(index + 1);
        }
    }

    private focusInput(index: number): void {
        this._inputElements.get(index)!.nativeElement.focus();
    }

    private buildFormArray(): FormArray<FormControl<string>> {
        const formControls: FormControl<string>[] = [];
        for (let i = 0; i < 6; i++) {
            const formControl = new FormControl<string>('', { nonNullable: true });
            //Create a subscription that propagates the new MFA code value whenever the control value changes.
            this._subscriptions.push(formControl.valueChanges.subscribe(() => this._propagateChanges?.(this.getMFACodeFromFormArray())));
            formControls.push(formControl);
        }
        return new FormArray<FormControl<string>>(formControls);
    }

    private setMFACodeValue(cleanMFACode: string): void {
        for (let i = 0; i < cleanMFACode.length; i++) {
            (this.formArray.controls[i]).setValue(cleanMFACode[i], { emitEvent: false }); //Don't trigger a value propagation for each individual control
        }
        //If the code is too short, delete everything after the end of the given code
        for (let i = cleanMFACode.length; i < this.formArray.controls.length; i++) {
            (this.formArray.controls[i]).setValue('', { emitEvent: false });
        }
    }

    private getMFACodeFromFormArray(): string {
        return this.formArray.controls.map(control => control.value).join('');
    }
}